import config from "config";
import { FACET_PATH_DELIMITER } from "constants/filter";
import { FILTER_TYPES as SAL_FILTER_TYPES } from "services/search/const";

/**
 * gets the name of the configured query filter
 *
 * @returns {String}
 */
export function getQueryFilterName() {
  for (let filterName in config.filters) {
    if (config.filters[filterName].type === SAL_FILTER_TYPES.QUERY) {
      return filterName;
    }
  }
  return undefined;
}

/**
 * Checks if query filter has a value
 *
 * @export
 * @param {*} filters
 * @returns
 */
export function queryFilterIsSet(filters) {
  const queryFilter = filters[getQueryFilterName()];

  return (queryFilter && queryFilter.values && queryFilter.values[0]) || false;
}
/**
 * get filter definitions for given type
 *
 * @param {SAL_FILTER_TYPES} type type of filter defined in @see services/search/const
 * @returns {filtername: {type: {SAL_FILTER_TYPES}, field: {string}}} relevant filterdefinitions @see config
 */
export function getFiltersByType(type) {
  let result = {};

  for (let filterName in config.filters) {
    if (config.filters[filterName].type === type) {
      result[filterName] = config.filters[filterName];
    }
  }

  return result;
}

/**
 * get all possible filters as name
 *
 * @param {Number} includeQueryName=true determines whether to include the queryFilter in result
 * @returns {Array} of filternames
 */
export function getFilterNames(includeQueryName = true) {
  if (includeQueryName) {
    return Object.keys(config.filters);
  }

  const queryfilterName = getQueryFilterName();

  return Object.keys(config.filters).filter((name) => {
    return name !== queryfilterName;
  });
}

/**
 * traverses the facetpathData recursivly along the selected values
 * and calls the given callback as well as returns its result.
 *
 * @param {[{value: String, label: String, count: Number, data: []}]} facetPathData the facetpath data from app state
 * @param {[String]} selectedValuePath with selected values on the path
 * @param {fn(entry: {label: string, value: string, count: number}, level: number)} callback the function to call
 * @param {Number} targetLevel=selectedValuePath.length targetLevel for recursion
 * @param {Number} currentLevel=0 level for recusion
 * @returns {Mixed} Value calculated by given callback function
 */
export function traverseFacepathSelection(
  facetPathData,
  selectedValuePath,
  callback,
  targetLevel = selectedValuePath.length,
  currentLevel = 0,
) {
  if (targetLevel === 0) {
    return callback(undefined, -1);
  }

  let result;

  for (let entry of facetPathData) {
    if (entry.value === selectedValuePath[currentLevel]) {
      if (currentLevel + 1 === targetLevel) {
        result = callback(entry, currentLevel);
        break;
      } else {
        result = traverseFacepathSelection(
          entry.data ? entry.data : [],
          selectedValuePath,
          callback,
          targetLevel,
          ++currentLevel,
        );
      }
    }
  }
  return result;
}
export function findFacetPathEntry(fpData, selectedValuePath) {
  return traverseFacepathSelection(fpData, selectedValuePath, (entry) => entry);
}

export function facetPathValueToArray(fpValue) {
  return fpValue ? fpValue.split(FACET_PATH_DELIMITER) : [];
}

/**
 * gets an array of filters initially set perconfig.
 * Only filters other than of type 'query' that have values are considered.
 *
 * @returns {Array.<{"filterName": {filterType: String, label: String, values: Array}}} array of filter objects
 */

export function getPresetFilterValues() {
  const filters = config.state.filters;
  const filtersWithValue = Object.keys(filters).reduce(
    (accumulator, filterKey) => {
      if (
        filterKey !== getQueryFilterName() &&
        filters[filterKey].values &&
        filters[filterKey].values.length
      ) {
        accumulator[filterKey] = filters[filterKey];
      }

      return accumulator;
    },
    {},
  );

  return filtersWithValue;
}

/**
 * Extracts the data of a filter value
 * @param  {string} value
 * @param {[Object]} filter the filter state of a single filter
 *
 * @returns {Object} a data point for the filter
 */
export function getDataPointForFilter(value, filter) {
  return (
    filter.data && filter.data.find((filterData) => filterData.value === value)
  );
}
